import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { installErrorCollector } from '@integrations/memo-project/browser';
import { MemoProjectErrorBoundary } from '@integrations/memo-project/react';
import { App } from './App';
import { LEGAL_PATHS, LegalPage } from './views/legal/LegalPage';
import { Landing } from './views/landing/Landing';
import { SUPPORT_ERROR_ENDPOINT } from './lib/integrations';
import { initPwa } from './lib/pwa';
import { StoreProvider } from './store/StoreProvider';
import { applyTheme, readTheme, watchSystem } from './store/theme';
import '@fontsource-variable/nunito';
import '@fontsource-variable/fredoka';
import './theme.css';

// Sugli host della landing (POPTOWN_LANDING_HOSTS) il server mette il meta `poptown-landing`: `/`
// è la landing e ogni altro percorso torna lì. Nessun rimando all'applicazione.
const landing = !!document.querySelector('meta[name=poptown-landing]');

// La landing pubblica è solo in tema chiaro (2026-11-01): lo fissa lo script di index.html, e qui
// non si tocca la scelta che la persona ha salvato per la plancia.
if (!landing) {
  const choice = readTheme();
  applyTheme(choice);
  watchSystem(choice, () => applyTheme(choice));
}

const root = document.getElementById('root');
if (!root) throw new Error('#root assente in index.html');

// Privacy e Termini sono pubbliche: si montano da sole, senza plancia, store, stream né API.
const path = window.location.pathname.replace(/\/+$/, '') || '/';
if (LEGAL_PATHS[path]) createRoot(root).render(<LegalPage path={path} />);
else if (landing && path === '/') createRoot(root).render(<Landing />);
else if (landing) window.location.replace('/');
else start(root);

function start(root: HTMLElement) {
  initPwa();

  // Gli errori che scappano nella pagina (eccezioni e promise rifiutate) vanno alla rotta
  // /api/support/error di PopTown, che li manda a Flow con il token: la pagina non lo vede mai.
  // I crash di render React non passano da window.onerror: li prende la boundary.
  installErrorCollector({ endpoint: SUPPORT_ERROR_ENDPOINT, source: 'Plancia' });

  const crashed = (
    <div style={{ padding: 40, color: 'var(--text)', background: 'var(--bg)', minHeight: '100vh' }}>
      <p style={{ margin: '0 0 14px' }}>Si è verificato un errore imprevisto. È stato segnalato.</p>
      <button onClick={() => window.location.reload()}>Ricarica la pagina</button>
    </div>
  );

  createRoot(root).render(
    <StrictMode>
      <MemoProjectErrorBoundary
        endpoint={SUPPORT_ERROR_ENDPOINT}
        source="Plancia"
        fallback={crashed}
      >
        <BrowserRouter>
          <StoreProvider>
            <App />
          </StoreProvider>
        </BrowserRouter>
      </MemoProjectErrorBoundary>
    </StrictMode>,
  );
}
